import { FC } from 'react';
import { WidgetCell, WidgetProps } from '../WidgetCell';
import { useIntl } from 'react-intl';
import { useDrawerContext } from '../../../../../drawer/DrawerContext';
import { Box, Stack, useTheme } from '@mui/material';
import { graphql } from '../../../../../gql';
import { useApiMutation } from '../../../../../query/hooks';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { z } from 'zod';
import { actionEditDrawerSchema } from './ActionEditDrawer';
import { AddActionSelect } from './AddActionSelect';
import { useTypedQueryParams } from '../../../../../hooks/useTypedQueryParams';
import { CellCard } from '../../components/CellCard';
import { LightbulbOutlined } from '@mui/icons-material';
import { isActionExpired } from '../../../../../actions/isActionExpired';
import slugify from 'slugify';
import { widgetDefinitions } from '../widgetDefinitions';
import { getExpiredBgColor } from '../../../../../utils/getExpiredBgColor';
import { useSnackbar } from '../../../../../store/snackbar';

const actionsWidget_addAction = graphql(`
	mutation actionsWidget_addAction($activityId: UUID!, $actionId: UUID!) {
		updateActivity(by: { id: $activityId }, data: { actions: { connect: { id: $actionId } } }) {
			ok
			errorMessage
		}
	}
`);

const actionsWidget_removeAction = graphql(`
	mutation actionsWidget_removeAction($activityId: UUID!, $actionId: UUID!) {
		updateActivity(by: { id: $activityId }, data: { actions: { disconnect: { id: $actionId } } }) {
			ok
			errorMessage
		}
	}
`);

const drawerSchema = z.object({
	drawer: z.union([actionEditDrawerSchema, z.object({})]).optional(),
});

export const ActionsCell: FC<WidgetProps> = ({ widget: { actions }, activityId, activityName }) => {
	const intl = useIntl();
	const theme = useTheme();
	const { setDrawerWithoutPrevParams } = useDrawerContext();
	const { openSnackbar } = useSnackbar();
	const { mutate: addAction } = useApiMutation(actionsWidget_addAction, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updateActivity.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'journey.detail.widget.actions.add.success' }),
					error: intl.formatMessage({ id: 'journey.detail.widget.actions.add.error' }),
				},
				errorMessage: result.updateActivity.errorMessage,
				openSnackbar,
			});
		},
	});
	const { mutate: removeAction, isPending: isRemovePending } = useApiMutation(actionsWidget_removeAction, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updateActivity.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'journey.detail.widget.actions.remove.success' }),
					error: intl.formatMessage({ id: 'journey.detail.widget.actions.remove.error' }),
				},
				errorMessage: result.updateActivity.errorMessage,
				openSnackbar,
			});
		},
	});

	const handleAddAction = (actionId: string) => {
		addAction({
			activityId,
			actionId,
		});
	};

	const handleOpenDrawer = (actionId: string) => {
		setDrawerWithoutPrevParams('editAction', { actionId, activityId });
	};

	const handleRemoveAction = (actionId: string) => {
		removeAction({
			activityId,
			actionId,
		});
	};

	const [{ drawer }] = useTypedQueryParams(drawerSchema);
	let actionId = null as string | null;
	if (drawer && typeof drawer === 'object' && 'actionId' in drawer) {
		actionId = drawer.actionId;
	}

	return (
		<WidgetCell
			data-activity-widget-name={slugify(activityName, { lower: true })}
			data-activity-widget-type={widgetDefinitions.actions.type}
			sx={{
				'& .add-action-button': { opacity: 0 },
				'&:hover .add-action-button': { opacity: 1 },
				'&:focus-within .add-action-button': { opacity: 1 },
			}}
		>
			<Stack direction={'column'} gap={1.5} alignItems={'stretch'}>
				{actions.map((action) => {
					const isExpired = isActionExpired(action.deadline ?? '');

					return (
						<CellCard
							key={action.id}
							name={action.name}
							description={action.description ?? ''}
							onEdit={() => handleOpenDrawer(action.id)}
							onRemove={() => handleRemoveAction(action.id)}
							icon={(props) => <LightbulbOutlined {...props} />}
							isActive={action.id === actionId}
							disabled={isRemovePending}
							backgroundColor={isExpired ? getExpiredBgColor(theme) : undefined}
						/>
					);
				})}
				<Box className={'add-action-button'}>
					<AddActionSelect removedIds={actions.map((action) => action.id)} onChange={handleAddAction} activityId={activityId} />
				</Box>
			</Stack>
		</WidgetCell>
	);
};
